import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Lock, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Achievement, AchievementProgress } from '@/hooks/useAchievements';

interface AchievementCardProps {
  achievement: Achievement;
  progress?: AchievementProgress | null;
  progressPercentage?: number;
}

const rarityColors = {
  common: 'bg-muted text-muted-foreground',
  rare: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  epic: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  legendary: 'bg-amber-500/10 text-amber-600 border-amber-500/20'
};

const rarityGlows = {
  common: '',
  rare: 'shadow-blue-500/20',
  epic: 'shadow-purple-500/20',
  legendary: 'shadow-amber-500/20 animate-pulse'
};

export function AchievementCard({ 
  achievement, 
  progress,
  progressPercentage = 0 
}: AchievementCardProps) {
  const isCompleted = progress?.completed || false;
  const isLocked = !progress || progress.current_progress === 0;
  const currentProgress = progress?.current_progress || 0;

  return (
    <Card className={cn(
      'relative overflow-hidden transition-all duration-300',
      isCompleted && 'border-primary bg-primary/5',
      isCompleted && rarityGlows[achievement.rarity as keyof typeof rarityGlows],
      !isCompleted && isLocked && 'opacity-60',
      !isCompleted && !isLocked && 'border-accent/30'
    )}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={cn(
            'text-4xl flex-shrink-0 transition-transform duration-300',
            isCompleted && 'scale-110',
            !isCompleted && isLocked && 'grayscale opacity-40'
          )}>
            {isLocked && achievement.is_hidden ? '🔒' : achievement.icon}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div>
                <h3 className={cn(
                  'font-semibold text-sm',
                  isCompleted && 'text-primary'
                )}>
                  {isLocked && achievement.is_hidden ? 'Hidden Achievement' : achievement.name}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isLocked && achievement.is_hidden 
                    ? 'Complete the requirements to unlock' 
                    : achievement.description}
                </p>
              </div>

              {isCompleted && (
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
              )}
              {isLocked && (
                <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              )}
            </div>

            {/* Progress Bar */}
            {!isCompleted && !isLocked && (
              <div className="mt-2 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    Progress: {currentProgress} / {achievement.requirement_value}
                  </span>
                  <span className="font-medium text-accent">
                    {Math.round(progressPercentage)}%
                  </span>
                </div>
                <Progress value={progressPercentage} className="h-2" />
              </div>
            )}

            {/* Badges */}
            <div className="flex items-center gap-2 mt-2">
              <Badge 
                variant="outline" 
                className={cn('text-xs', rarityColors[achievement.rarity as keyof typeof rarityColors])}
              >
                {achievement.rarity.charAt(0).toUpperCase() + achievement.rarity.slice(1)}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {achievement.points} pts
              </Badge>
              {isCompleted && progress?.completed_at && (
                <span className="text-xs text-muted-foreground">
                  {new Date(progress.completed_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
